import { useCallback, useEffect, useMemo, useRef } from 'react';

import { FeatureMode } from '../types/types';

/**
 * Base interface that all tab props should extend
 */
export interface BaseTabProps<TData, TDraftData> {
  mode: FeatureMode;
  featureData?: TData;
  draftData?: Partial<TDraftData>;
  /**
   * Callback to notify parent of form state changes
   * @param isDirty - Whether the form has unsaved changes
   * @param hasChanges - Whether the form has any changes (for confirmation dialogs)
   * @param isValid - Whether the form is valid
   * @param hasOnlyNonVersionedChanges - Whether only non-versioned fields changed (skips conflict detection)
   */
  onFormStateChange?: (
    isDirty: boolean,
    hasChanges?: boolean,
    isValid?: boolean,
    hasOnlyNonVersionedChanges?: boolean
  ) => void;
}

/**
 * Configuration for base tab behavior
 */
export interface BaseTabConfig<TData> {
  /**
   * Function to determine if entity is archived
   */
  isArchivedFn?: (entityData?: TData) => boolean;

  /**
   * Custom logic to calculate dirty state (for form validation/save button)
   */
  calculateDirtyStateFn?: () => boolean;

  /**
   * Custom logic to calculate pure changes state (for confirmation dialogs)
   */
  calculateHasChangesFn?: () => boolean;

  /**
   * Custom logic to calculate form validity state
   */
  calculateIsValidFn?: () => boolean;

  /**
   * Custom logic to calculate if only non-versioned fields changed
   * Returns the computed value directly (not a function to call)
   */
  hasOnlyNonVersionedChangesValue?: boolean;

  /**
   * Debounce delay for form state change notifications (default: 0ms)
   */
  formStateDebounceMs?: number;
}

/**
 * Return interface for base tab hook
 */
export interface BaseTabReturn {
  /** Whether the entity is archived */
  isArchivedEntity: boolean;

  /** Whether the tab is in read-only mode */
  isReadMode: boolean;

  /** Function to notify parent of form state changes */
  notifyFormStateChange: (
    isDirty: boolean,
    hasChanges?: boolean,
    isValid?: boolean,
    hasOnlyNonVersionedChanges?: boolean
  ) => void;
}

/**
 * Base hook that provides common functionality for all feature tabs.
 * Extracts shared logic for mode handling, archived state, and form notifications.
 *
 * @example
 * ```typescript
 * function MyTab({ mode, featureData, draftData, onFormStateChange }: MyTabProps) {
 *   const { isReadMode, isArchivedEntity, notifyFormStateChange } = useBaseTab({
 *     mode,
 *     featureData,
 *     draftData,
 *     onFormStateChange
 *   }, {
 *     isArchivedFn: (data) => data?.status === 'archived',
 *     calculateDirtyStateFn: () => form.formState.isDirty
 *   });
 *
 *   // Your tab-specific logic here
 * }
 * ```
 */
export function useBaseTab<TData, TDraftData>(
  props: BaseTabProps<TData, TDraftData>,
  config: BaseTabConfig<TData> = {}
): BaseTabReturn {
  const { mode, featureData, onFormStateChange } = props;
  const {
    isArchivedFn = () => false,
    calculateDirtyStateFn,
    calculateHasChangesFn,
    calculateIsValidFn,
    hasOnlyNonVersionedChangesValue,
    formStateDebounceMs = 0,
  } = config;

  /**
   * Determine if the entity is archived
   */
  const isArchivedEntity = useMemo(() => {
    return isArchivedFn(featureData);
  }, [featureData, isArchivedFn]);

  /**
   * Determine if the tab should be in read-only mode
   */
  const isReadMode = useMemo(() => {
    return mode === FeatureMode.Read || isArchivedEntity;
  }, [mode, isArchivedEntity]);

  /**
   * Debounced form state change notification
   */
  const notifyFormStateChange = useCallback(
    (
      isDirty: boolean,
      hasChanges?: boolean,
      isValid?: boolean,
      hasOnlyNonVersionedChanges?: boolean
    ) => {
      if (!onFormStateChange) return;

      if (formStateDebounceMs > 0) {
        const timer = setTimeout(() => {
          onFormStateChange(isDirty, hasChanges, isValid, hasOnlyNonVersionedChanges);
        }, formStateDebounceMs);

        return () => clearTimeout(timer);
      } else {
        onFormStateChange(isDirty, hasChanges, isValid, hasOnlyNonVersionedChanges);
      }
    },
    [onFormStateChange, formStateDebounceMs]
  );

  // Track previously reported values to avoid re-render loops caused by
  // form.watch() returning new object references on every render
  const lastReportedState = useRef<{
    isDirty: boolean;
    hasChanges: boolean | undefined;
    isValid: boolean | undefined;
    hasOnlyNonVersionedChanges: boolean | undefined;
  }>({
    isDirty: false,
    hasChanges: undefined,
    isValid: undefined,
    hasOnlyNonVersionedChanges: undefined,
  });

  /**
   * Effect to notify parent of form state changes when calculateDirtyStateFn is provided.
   * Compares computed values with the last reported ones to prevent infinite re-render loops.
   */
  useEffect(() => {
    if (calculateDirtyStateFn && onFormStateChange) {
      const timer = setTimeout(() => {
        const isDirty = calculateDirtyStateFn();
        const hasChanges = calculateHasChangesFn ? calculateHasChangesFn() : undefined;
        const isValid = calculateIsValidFn ? calculateIsValidFn() : undefined;
        const prev = lastReportedState.current;

        // Only notify parent if computed values actually changed
        if (
          prev.isDirty !== isDirty ||
          prev.hasChanges !== hasChanges ||
          prev.isValid !== isValid ||
          prev.hasOnlyNonVersionedChanges !== hasOnlyNonVersionedChangesValue
        ) {
          lastReportedState.current = {
            isDirty,
            hasChanges,
            isValid,
            hasOnlyNonVersionedChanges: hasOnlyNonVersionedChangesValue,
          };
          onFormStateChange(isDirty, hasChanges, isValid, hasOnlyNonVersionedChangesValue);
        }
      }, formStateDebounceMs);

      return () => clearTimeout(timer);
    }
  }, [
    calculateDirtyStateFn,
    calculateHasChangesFn,
    calculateIsValidFn,
    hasOnlyNonVersionedChangesValue,
    onFormStateChange,
    formStateDebounceMs,
  ]);

  return {
    isArchivedEntity,
    isReadMode,
    notifyFormStateChange,
  };
}
